import ApiBase from './base/ApiBase';

export interface EmployeeEmployerSummary {
    EmploymentId: string;
    OrganizationName: string;
    PositionTitle: string;
    IsActive: boolean;
    StartedDate?: string | null;
    EndedDate?: string | null;
    LastReviewDate?: string | null;
    ReviewCount: number;
}

export interface EmployeePortalSummary {
    Employers: EmployeeEmployerSummary[];
    DefaultEmploymentId?: string | null;
}

export interface EmployeeReviewSummary {
    Id: string;
    TemplateName: string;
    OverallScore: number;
    PublishedDate: string;
}

export interface EmployeeReviewItem {
    Name: string;
    Type: string;
    Selection: string;
}

export interface EmployeeReviewGroup {
    Name: string;
    Score: number;
    Items: EmployeeReviewItem[];
}

export interface EmployeeReviewDetail extends EmployeeReviewSummary {
    OrganizationName: string;
    PositionTitle: string;
    Groups: EmployeeReviewGroup[];
}

export type EmployeeInvitationState = 'ReadyForConfirmation' | 'Claimed' | 'Expired' | 'Revoked' | 'Replayed';

export interface EmployeeInvitationConfirmation {
    State: EmployeeInvitationState;
    ExpiresAt: string;
}

export interface EmployeeInvitationSession {
    Confirmation: EmployeeInvitationConfirmation;
    SessionHandle: string;
}

export interface EmployeeInvitationAdminStatus {
    InvitationId: string;
    TalentId: string;
    Status: 'Pending' | 'Claimed' | 'Expired' | 'Revoked' | 'Rejected';
    AccessStatus: 'NotConnected' | 'Active' | 'Revoked';
    AccessRevokedDate?: string | null;
    DeliveryEmail: string;
    CreatedDate: string;
    ExpiresDate: string;
    ClaimedDate?: string | null;
    RevokedDate?: string | null;
}

/**
 * Employee self-service data is deliberately isolated from the tenant-wide
 * Talent and Assessment clients. These endpoints return only the projection
 * the signed-in review subject is permitted to see.
 */
class EmployeePortalClient extends ApiBase {
    private controllerName = 'EmployeePortal';

    async GetSummary(signal?: AbortSignal): Promise<EmployeePortalSummary> {
        return this.GetAsync(`${this.controllerName}/Contexts`, signal);
    }

    async GetReviews(employmentId: string, signal?: AbortSignal): Promise<EmployeeReviewSummary[]> {
        return this.GetAsync(
            `${this.controllerName}/Employments/${encodeURIComponent(employmentId)}/Reviews`,
            signal,
        );
    }

    async GetReview(
        employmentId: string,
        reviewId: string,
        signal?: AbortSignal,
    ): Promise<EmployeeReviewDetail> {
        return this.GetAsync(
            `${this.controllerName}/Employments/${encodeURIComponent(employmentId)}/Reviews/${encodeURIComponent(reviewId)}`,
            signal,
        );
    }

    async InviteReviewSubject(talentId: string, deliveryEmail: string, signal?: AbortSignal): Promise<EmployeeInvitationAdminStatus> {
        return this.PostAsync(`${this.controllerName}/Administration/Invitations`, {
            TalentId: talentId,
            DeliveryEmail: deliveryEmail,
        }, signal);
    }

    async BeginInvitationSession(invitationToken: string, idToken: string, signal?: AbortSignal): Promise<EmployeeInvitationSession> {
        const result = await this.PostPublicWithResponseHeaderAsync(`${this.controllerName}/Invitation/Session`, {
            InvitationToken: invitationToken,
            IdToken: idToken,
        }, 'X-Employee-Invitation-Session', signal);
        if (!result.Header)
            throw new Error('The invitation session handle is missing.');
        return { Confirmation: result.Data, SessionHandle: result.Header };
    }

    async ClaimInvitation(idToken: string, sessionHandle: string, signal?: AbortSignal): Promise<EmployeeEmployerSummary> {
        return this.PostPublicAsync(`${this.controllerName}/Invitation/Confirm`, {
            IdToken: idToken,
            Confirm: true,
        }, signal, [['X-Employee-Invitation-Session', sessionHandle]]);
    }

    async GetInvitationStatusForTalent(talentId: string, signal?: AbortSignal): Promise<EmployeeInvitationAdminStatus[]> {
        return this.GetAsync(`${this.controllerName}/Administration/Talent/${encodeURIComponent(talentId)}/Invitations`, signal);
    }

    async ResendInvitation(talentId: string, signal?: AbortSignal): Promise<EmployeeInvitationAdminStatus> {
        return this.PostAsync(`${this.controllerName}/Administration/Talent/${encodeURIComponent(talentId)}/Invitations/Resend`, null, signal);
    }

    async RevokeAccess(talentId: string, reason: string, signal?: AbortSignal): Promise<void> {
        return this.PostAsync(`${this.controllerName}/Administration/Talent/${encodeURIComponent(talentId)}/Access/Revoke`, { Reason: reason }, signal);
    }
}

export default EmployeePortalClient;
